import { useCallback, useEffect, useState } from 'react';
import { Logger } from '@shared/lib/logger';

const logger = new Logger('storage');

export interface StorageEstimateInfo {
	usage: number | undefined;
	quota: number | undefined;
}

/**
 * Imperative core of `usePersistentStorage`, exported so it can be called at
 * a moment of *demonstrated intent* rather than only at boot.
 *
 * Resolves `true` if storage is persistent afterwards (already-persisted
 * counts), `false` if the request was denied or the API is unavailable.
 * Never rejects -- callers are fire-and-forget and a failure here must not
 * break the action that triggered it.
 */
export async function requestPersistentStorage(): Promise<boolean> {
	if (!('storage' in navigator) || !('persist' in navigator.storage)) {
		return false;
	}
	try {
		if (await navigator.storage.persisted()) {
			return true;
		}
		const granted = await navigator.storage.persist();
		logger.info(`Persistent storage ${granted ? 'granted' : 'denied'}`);
		return granted;
	} catch (error: unknown) {
		logger.error('Failed to request persistent storage', error);
		return false;
	}
}

/**
 * Requests persistent storage so a reader's downloaded issues aren't the
 * first thing evicted under storage pressure. Best-effort only -- browsers
 * grant this on engagement heuristics, never guaranteed.
 *
 * **Deliberately still gated on `enabled` (installed/standalone) rather than
 * called unconditionally** (topic 02's "request persistence for non-installed
 * readers too", resolved 2026-08-28). Flipping this to always-on at boot is
 * the obvious reading of that item and it is the wrong fix: `persist()` is
 * silent-and-heuristic in Chrome and Safari, but **Firefox shows a permission
 * prompt**. An unconditional boot-time call therefore prompts every
 * non-installed Firefox reader on their first page view, before they have
 * done anything that would explain what the prompt is for -- which both reads
 * as hostile and is the case most likely to be denied, permanently.
 *
 * The gap the item is really about (a browser-tab reader's saved content
 * staying evictable) is closed instead by calling
 * `requestPersistentStorage()` at the point of intent: favouriting an
 * article, i.e. the one action in this app that means "keep this for me
 * offline" (see CLAUDE.md's Dexie-vs-Query ownership split). At that moment a
 * prompt is contextual, explicable, and far likelier to be granted.
 */
export function usePersistentStorage(enabled: boolean) {
	useEffect(() => {
		if (!enabled) {
			return;
		}
		void requestPersistentStorage();
	}, [enabled]);
}

/**
 * Surfaces navigator.storage.estimate() (usage/quota bytes) for
 * settings/downloads UI. Call refresh() after a download completes to get
 * an up-to-date number.
 *
 * No UI consumer yet (confirmed by grep, 2026-08-16) -- topic 20
 * (App-like PWA Features) owns wiring this into a real downloads/storage
 * view. Flagged rather than deleted since it's real, working, purpose-built
 * infrastructure waiting on that feature, not orphaned code.
 */
export function useStorageEstimate(): StorageEstimateInfo & {
	refresh: () => void;
} {
	const [estimate, setEstimate] = useState<StorageEstimateInfo>({
		usage: undefined,
		quota: undefined,
	});

	const refresh = useCallback(() => {
		if (!('storage' in navigator) || !('estimate' in navigator.storage)) {
			return;
		}
		navigator.storage
			.estimate()
			.then((result) => {
				setEstimate({ usage: result.usage, quota: result.quota });
			})
			.catch((error: unknown) => {
				logger.error('Failed to estimate storage usage', error);
			});
	}, []);

	useEffect(() => {
		refresh();
	}, [refresh]);

	return { ...estimate, refresh };
}
